import React, { useEffect, useRef, useState } from 'react';
import {
  BarChart3,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Compass,
  GitCompare,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AnalyticsCards } from './AnalyticsCards';
import { CapacityCalendarFrame } from './CapacityCalendarFrame';
import { CommitmentMetricTiles } from './CommitmentMetricTiles';
import { DeliveryReviewHeroMockup } from './DeliveryReviewHeroMockup';
import { ForecastSpotlightCard } from './ForecastSpotlightCard';
import { ReleasePlanCard } from './ReleasePlanCard';
import { trackEvent } from '../../../shared/analytics';
import { solutionsAlsoIncluded } from '../../../shared/config/solutionsContent';

/**
 * Raycast-style feature picker for the Solutions page.
 *
 * All six panels stay in the DOM (visual toggle only) so the prerendered
 * static twin keeps full feature copy for SEO. Panel swap is a fade gated
 * by `prefers-reduced-motion` in CSS.
 *
 * SPA passes `onAuditClick` (scroll + focus the audit form); the static
 * prerender omits it and panels render plain `#delivery-audit` anchors.
 *
 * RLX-FEAT-008: the SPA also passes `lazyMockups` — a panel's mockup (and its
 * images) mounts only after the panel's first activation, so hidden tabs cost
 * no network. The static twin renders every mockup for complete HTML.
 */

type FeatureId =
  | 'delivery-review'
  | 'forecast'
  | 'commitment'
  | 'calendar'
  | 'analytics'
  | 'release-plan';

type FeatureDef = {
  id: FeatureId;
  tabLabel: string;
  Icon: LucideIcon;
  eyebrow: string;
  eyebrowVariant: 'review' | 'forecast' | 'commitment' | 'calendar' | 'analytics' | 'release';
  heading: string;
  sub: string;
  /** One-line business takeaway under the visual. */
  takeaway: string;
  ctaLabel?: string;
  Mockup: React.ComponentType;
};

const HERO_FLOAT_CARDS: readonly {
  id: string;
  label: string;
  value: string;
  /** Where the number comes from — keeps the floats honest. */
  context: string;
  Icon: LucideIcon;
  variant: string;
}[] = [
  {
    id: 'forecast',
    label: 'Forecast remaining',
    value: '98.5d',
    context: 'across the next 3 sprints',
    Icon: TrendingUp,
    variant: 'forecast',
  },
  {
    id: 'commitment',
    label: 'Commitment reliability',
    value: '78%',
    context: 'vs 90% target',
    Icon: ShieldCheck,
    variant: 'commitment',
  },
  {
    id: 'capacity',
    label: 'Team available capacity',
    value: '18.5d',
    context: 'this sprint, after absences',
    Icon: Calendar,
    variant: 'capacity',
  },
] as const;

/** Delivery Review visual: score mockup + floating signal cards. */
function DeliveryReviewVisual(): React.ReactElement {
  return (
    <div className="solutions-hero-dr-stack solutions-explorer__dr-stack">
      <div className="solutions-hero-dr-glow" aria-hidden="true" />
      <DeliveryReviewHeroMockup />
      <ul className="solutions-hero-dr-floats" aria-label="Live delivery signals">
        {HERO_FLOAT_CARDS.map((card) => (
          <li key={card.id} className={`dr-hero-float-card dr-hero-float-card--${card.variant}`}>
            <span className="dr-hero-float-card__icon" aria-hidden="true">
              <card.Icon size={16} strokeWidth={1.6} />
            </span>
            <span className="dr-hero-float-card__body">
              <span className="dr-hero-float-card__value">{card.value}</span>
              <span className="dr-hero-float-card__label">{card.label}</span>
              <span className="dr-hero-float-card__context">{card.context}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const FEATURES: readonly FeatureDef[] = [
  {
    id: 'delivery-review',
    tabLabel: 'Delivery Review',
    Icon: ClipboardCheck,
    eyebrow: 'DELIVERY REVIEW · THE REALITY',
    eyebrowVariant: 'review',
    heading: 'Decide what ships next.',
    sub: 'Relix turns Jira signals into a delivery health score with a prioritized decision queue — so the next iteration starts from facts, not feelings.',
    takeaway: 'One score to stand behind in the release meeting.',
    ctaLabel: 'Get a Free Delivery Audit',
    Mockup: DeliveryReviewVisual,
  },
  {
    id: 'forecast',
    tabLabel: 'Forecast',
    Icon: Compass,
    eyebrow: 'FORECAST · PRE-COMMITMENT',
    eyebrowVariant: 'forecast',
    heading: 'Forecast what can actually ship.',
    sub: 'Before the promise is made, Relix shows whether planned scope fits real capacity, estimates, bug buffer, and remaining delivery time.',
    takeaway: 'A ship-date answer you can defend before you promise it.',
    ctaLabel: 'See your Forecast in a free audit',
    Mockup: ForecastSpotlightCard,
  },
  {
    id: 'commitment',
    tabLabel: 'Commitment',
    Icon: ShieldCheck,
    eyebrow: 'COMMITMENT · POST-PROMISE CONTROL',
    eyebrowVariant: 'commitment',
    heading: 'See what changed after the promise.',
    sub: 'Freeze the baseline, then track what stayed, what was added, and what was removed from the release.',
    takeaway: 'Scope drift stops being a surprise after the baseline.',
    ctaLabel: 'Review your Commitment in a free audit',
    Mockup: CommitmentMetricTiles,
  },
  {
    id: 'calendar',
    tabLabel: 'Calendar',
    Icon: Calendar,
    eyebrow: 'CALENDAR · REAL CAPACITY',
    eyebrowVariant: 'calendar',
    heading: 'See real team availability.',
    sub: "Relix brings absences, holidays, and team events into one calendar — and turns them into the team's real capacity for every release decision.",
    takeaway: 'Plans stop assuming people who are not there.',
    ctaLabel: 'Check your real Capacity in a free audit',
    Mockup: CapacityCalendarFrame,
  },
  {
    id: 'analytics',
    tabLabel: 'Analytics',
    Icon: BarChart3,
    eyebrow: 'ANALYTICS THAT EXPLAIN RISK',
    eyebrowVariant: 'analytics',
    heading: 'When risk appears, see why.',
    sub: 'Relix connects estimation, quality, and capacity signals to explain what is driving delivery risk.',
    takeaway: 'Risk arrives with an explanation attached.',
    ctaLabel: 'Get your risk Analytics in a free audit',
    Mockup: AnalyticsCards,
  },
  {
    id: 'release-plan',
    tabLabel: 'Release Plan',
    Icon: GitCompare,
    eyebrow: 'PLAN WITH CONFIDENCE',
    eyebrowVariant: 'release',
    heading: 'Plan releases with visible capacity.',
    sub: 'See how releases line up over time, where capacity is already committed, and which release is most likely to become a delivery surprise.',
    takeaway: 'The next delivery surprise is visible before it is committed.',
    ctaLabel: 'Plan your releases in a free audit',
    Mockup: ReleasePlanCard,
  },
] as const;

const DEFAULT_TAB: FeatureId = 'delivery-review';

type SolutionsFeatureExplorerProps = {
  /** SPA-only: smooth-scroll to the audit form and focus the email input. */
  onAuditClick?: () => void;
  /**
   * SPA-only: mount a panel's mockup on first activation instead of upfront.
   * The prerendered static twin leaves this off so all mockups (and their
   * alt copy) exist in the served HTML.
   */
  lazyMockups?: boolean;
};

export function SolutionsFeatureExplorer({
  onAuditClick,
  lazyMockups = false,
}: SolutionsFeatureExplorerProps): React.ReactElement {
  const [active, setActive] = useState<FeatureId>(DEFAULT_TAB);
  const [visited, setVisited] = useState<ReadonlySet<FeatureId>>(() => new Set([DEFAULT_TAB]));
  const barRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });

  const activate = (id: FeatureId) => {
    if (id === active) return;
    setActive(id);
    setVisited((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    trackEvent('solutions_explorer_tab', { tab: id });
  };

  const updateScrollEdges = () => {
    const bar = barRef.current;
    if (!bar) return;
    setScrollEdges({
      left: bar.scrollLeft > 4,
      right: bar.scrollLeft < bar.scrollWidth - bar.clientWidth - 4,
    });
  };

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    updateScrollEdges();
    const ro = new ResizeObserver(updateScrollEdges);
    ro.observe(bar);
    window.addEventListener('resize', updateScrollEdges);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateScrollEdges);
    };
  }, []);

  const bringTabIntoView = (id: FeatureId) => {
    document
      .getElementById(`explorer-tab-${id}`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const stepTab = (dir: 1 | -1) => {
    const ids = FEATURES.map((f) => f.id);
    const idx = ids.indexOf(active);
    const next = ids[(idx + dir + ids.length) % ids.length];
    activate(next);
    bringTabIntoView(next);
  };

  const scrollBarBy = (dir: 1 | -1) => {
    const bar = barRef.current;
    if (!bar) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    bar.scrollBy({ left: dir * 220, behavior: reduced ? 'auto' : 'smooth' });
  };

  const onPanelsTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    if (!t) return;
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const onPanelsTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      stepTab(dx < 0 ? 1 : -1);
    }
  };

  const onBarKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const ids = FEATURES.map((f) => f.id);
    const idx = ids.indexOf(active);
    let next: FeatureId | null = null;
    if (e.key === 'ArrowRight') next = ids[(idx + 1) % ids.length];
    else if (e.key === 'ArrowLeft') next = ids[(idx - 1 + ids.length) % ids.length];
    else if (e.key === 'Home') next = ids[0];
    else if (e.key === 'End') next = ids[ids.length - 1];
    if (next) {
      e.preventDefault();
      activate(next);
      document.getElementById(`explorer-tab-${next}`)?.focus();
    }
  };

  return (
    <section
      id="solutions-feature-explorer"
      className="solutions-feature-explorer"
      aria-label="Relix platform features"
    >
      <span className="solutions-section-eyebrow solutions-explorer__eyebrow">THE PLATFORM</span>
      <div
        className={`solutions-explorer__bar-wrap${scrollEdges.left ? ' is-scrollable-left' : ''}${
          scrollEdges.right ? ' is-scrollable-right' : ''
        }`}
      >
        <div
          ref={barRef}
          className="solutions-explorer__bar"
          role="tablist"
          aria-label="Delivery features"
          onKeyDown={onBarKeyDown}
          onScroll={updateScrollEdges}
        >
          {FEATURES.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              id={`explorer-tab-${f.id}`}
              aria-selected={active === f.id}
              aria-controls={`explorer-panel-${f.id}`}
              tabIndex={active === f.id ? 0 : -1}
              className="solutions-explorer__tab"
              onClick={() => {
                activate(f.id);
                bringTabIntoView(f.id);
              }}
            >
              <f.Icon size={15} strokeWidth={1.7} aria-hidden="true" />
              <span>{f.tabLabel}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          tabIndex={-1}
          className="solutions-explorer__scroll-btn solutions-explorer__scroll-btn--left"
          aria-label="Scroll tabs left"
          onClick={() => scrollBarBy(-1)}
        >
          <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          className="solutions-explorer__scroll-btn solutions-explorer__scroll-btn--right"
          aria-label="Scroll tabs right"
          onClick={() => scrollBarBy(1)}
        >
          <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
      <div
        className="solutions-explorer__panels"
        onTouchStart={onPanelsTouchStart}
        onTouchEnd={onPanelsTouchEnd}
      >
        {FEATURES.map((f) => (
          <div
            key={f.id}
            role="tabpanel"
            id={`explorer-panel-${f.id}`}
            aria-labelledby={`explorer-tab-${f.id}`}
            tabIndex={0}
            className="solutions-explorer__panel"
            data-active={active === f.id}
          >
            <header className="solutions-section-header solutions-section-header--centered">
              <span
                className={`solutions-section-eyebrow solutions-section-eyebrow--${f.eyebrowVariant}`}
              >
                {f.eyebrow}
              </span>
              <h2>{f.heading}</h2>
              <p className="solutions-section-sub">{f.sub}</p>
            </header>
            {!lazyMockups || visited.has(f.id) ? <f.Mockup /> : null}
            <p className="solutions-explorer__takeaway">{f.takeaway}</p>
            {f.ctaLabel ? (
              <div className="solutions-explorer__panel-cta">
                {onAuditClick ? (
                  <button
                    type="button"
                    className="solutions-section-cta"
                    onClick={() => {
                      trackEvent('solutions_explorer_cta', { tab: f.id });
                      onAuditClick();
                    }}
                  >
                    {f.ctaLabel}
                    <span aria-hidden="true">→</span>
                  </button>
                ) : (
                  <a href="#delivery-audit" className="solutions-section-cta">
                    {f.ctaLabel}
                    <span aria-hidden="true">→</span>
                  </a>
                )}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <div className="solutions-explorer__also">
        <span className="solutions-explorer__also-label">{solutionsAlsoIncluded.label}</span>
        <ul className="solutions-explorer__also-list">
          {solutionsAlsoIncluded.items.map((item) => (
            <li key={item} className="solutions-explorer__also-chip">
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
